(() => {
  const script = document.currentScript;
  const gtmId = script && script.dataset.gtmId;
  const googleAdsId = script && script.dataset.googleAdsId;
  const oneTrustDomainId = script && script.dataset.oneTrustDomainId;
  const confirmationKey = 'brxl_ca_privacy_choice_confirmed';
  const oneTrustInteractionEvents = ['scroll', 'click', 'touchstart'];
  const state = {
    googleAdsLoaded: false,
    gtmLoaded: false,
    openPreferencesWhenReady: false,
    oneTrustBound: false,
    oneTrustInteractionBound: false,
    oneTrustLoaded: false,
    oneTrustReadyAt: 0,
    preferencesOpenLoopActive: false,
    regionResolved: false,
    trackersScheduled: false
  };

  const whenDomReady = new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', resolve, { once: true });
      return;
    }
    resolve();
  });

  function getOneTrust() {
    return window.OneTrust || window.Optanon || null;
  }

  function getActiveGroups() {
    return window.OnetrustActiveGroups || window.OptanonActiveGroups || '';
  }

  function isGroupActive(groupId) {
    return (',' + getActiveGroups().replace(/^,|,$/g, '') + ',').includes(',' + groupId + ',');
  }

  function hasConfirmedChoice() {
    try {
      return window.localStorage.getItem(confirmationKey) === '1';
    } catch (error) {
      return false;
    }
  }

  function setConfirmedChoice(confirmed) {
    try {
      if (confirmed) {
        window.localStorage.setItem(confirmationKey, '1');
      } else {
        window.localStorage.removeItem(confirmationKey);
      }
    } catch (error) {
      return;
    }
  }

  function hasGpcSignal() {
    return navigator.globalPrivacyControl === true;
  }

  function isAlertBoxClosed() {
    const oneTrust = getOneTrust();
    return Boolean(oneTrust && typeof oneTrust.IsAlertBoxClosed === 'function' && oneTrust.IsAlertBoxClosed());
  }

  function loadOneTrust() {
    if (state.oneTrustLoaded || !oneTrustDomainId) return;
    state.oneTrustLoaded = true;
    if (document.querySelector('script[src*="cdn.cookielaw.org/scripttemplates/otSDKStub.js"]')) return;
    const oneTrustScript = document.createElement('script');
    oneTrustScript.async = true;
    oneTrustScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    oneTrustScript.type = 'text/javascript';
    oneTrustScript.charset = 'UTF-8';
    oneTrustScript.setAttribute('data-domain-script', oneTrustDomainId);
    document.head.appendChild(oneTrustScript);
  }

  function openPreferencesWhenReady(attempt = 0) {
    if (!state.openPreferencesWhenReady) {
      state.preferencesOpenLoopActive = false;
      return;
    }
    const oneTrust = getOneTrust();
    const preferenceCenter = document.getElementById('onetrust-pc-sdk');
    if (preferenceCenter && window.getComputedStyle(preferenceCenter).display !== 'none') {
      state.openPreferencesWhenReady = false;
      state.preferencesOpenLoopActive = false;
      return;
    }
    const oneTrustReady = Boolean(window.BRXLPrivacy && window.BRXLPrivacy.oneTrustReady);
    const oneTrustSettled = oneTrustReady && state.oneTrustReadyAt > 0 && Date.now() - state.oneTrustReadyAt >= 500;
    if (oneTrustSettled && oneTrust && typeof oneTrust.ToggleInfoDisplay === 'function' && preferenceCenter) {
      oneTrust.ToggleInfoDisplay();
      window.setTimeout(() => openPreferencesWhenReady(attempt + 1), 500);
      return;
    }
    if (attempt >= 50) {
      state.openPreferencesWhenReady = false;
      state.preferencesOpenLoopActive = false;
      return;
    }
    window.setTimeout(() => openPreferencesWhenReady(attempt + 1), 100);
  }

  function startOpenPreferencesLoop() {
    if (state.preferencesOpenLoopActive) return;
    state.preferencesOpenLoopActive = true;
    openPreferencesWhenReady();
  }

  function loadOneTrustOnInteraction() {
    oneTrustInteractionEvents.forEach((eventName) => {
      document.removeEventListener(eventName, loadOneTrustOnInteraction);
    });
    state.oneTrustInteractionBound = false;
    loadOneTrust();
  }

  function deferOneTrustUntilInteraction() {
    if (state.oneTrustInteractionBound || state.oneTrustLoaded || getOneTrust()) return;
    state.oneTrustInteractionBound = true;
    whenDomReady.then(() => {
      oneTrustInteractionEvents.forEach((eventName) => {
        document.addEventListener(eventName, loadOneTrustOnInteraction, { once: true, passive: true });
      });
    });
  }

  function loadGtm() {
    if (state.gtmLoaded || !gtmId) return;
    state.gtmLoaded = true;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const firstScript = document.getElementsByTagName('script')[0];
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(gtmId);
    firstScript.parentNode.insertBefore(gtmScript, firstScript);
  }

  function loadGoogleAds() {
    if (state.googleAdsLoaded || !googleAdsId) return;
    state.googleAdsLoaded = true;
    const googleScript = document.createElement('script');
    googleScript.async = true;
    googleScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(googleAdsId);
    document.head.appendChild(googleScript);
    window.gtag('js', new Date());
    window.gtag('config', googleAdsId);
  }

  function scheduleTrackers() {
    if (state.trackersScheduled) return;
    state.trackersScheduled = true;
    whenDomReady.then(() => {
      window.setTimeout(loadGtm, 2000);
      window.setTimeout(loadGoogleAds, 3000);
    });
  }

  function readShopifyRegion() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.getRegion === 'function') {
        return window.Shopify.customerPrivacy.getRegion() || '';
      }
    } catch (error) {
      return '';
    }
    return '';
  }

  function readOneTrustRegion() {
    try {
      const oneTrust = getOneTrust();
      if (!oneTrust || typeof oneTrust.getGeolocationData !== 'function') return '';
      const location = oneTrust.getGeolocationData();
      if (!location || !location.country) return '';
      return String(location.country).toUpperCase() + String(location.state || '').toUpperCase();
    } catch (error) {
      return '';
    }
  }

  function readRegion() {
    return readOneTrustRegion() || readShopifyRegion();
  }

  function isCaliforniaRegion(region) {
    return region === 'USCA';
  }

  function updateGoogleConsent() {
    const confirmed = hasConfirmedChoice() || hasGpcSignal();
    const alertClosed = isAlertBoxClosed() || hasGpcSignal();
    const analyticsAllowed = confirmed && alertClosed && isGroupActive('C0002');
    const marketingAllowed = confirmed && alertClosed && isGroupActive('C0004') && !hasGpcSignal();
    window.gtag('consent', 'update', {
      ad_storage: marketingAllowed ? 'granted' : 'denied',
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_user_data: marketingAllowed ? 'granted' : 'denied',
      ad_personalization: marketingAllowed ? 'granted' : 'denied'
    });
    if (marketingAllowed) scheduleTrackers();
  }

  function syncShopifyConsent() {
    if (!hasConfirmedChoice() || !isAlertBoxClosed()) return;
    const customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== 'function') return;
    const desired = {
      analytics: isGroupActive('C0002'),
      marketing: isGroupActive('C0004') && !hasGpcSignal(),
      preferences: isGroupActive('C0003'),
      sale_of_data: !isGroupActive('BG39')
    };
    const current = typeof customerPrivacy.currentVisitorConsent === 'function'
      ? customerPrivacy.currentVisitorConsent()
      : {};
    const changed = Object.keys(desired).some((key) => current[key] !== (desired[key] ? 'yes' : 'no'));
    if (changed) customerPrivacy.setTrackingConsent(desired, () => {});
  }

  function applyCaliforniaConsent() {
    document.documentElement.classList.add('privacy-region-us-ca');
    updateGoogleConsent();
    syncShopifyConsent();
  }

  function bindOneTrust() {
    if (state.oneTrustBound) return;
    const oneTrust = getOneTrust();
    if (!oneTrust) return;
    state.oneTrustBound = true;
    if (typeof oneTrust.OnConsentChanged === 'function') {
      oneTrust.OnConsentChanged(() => {
        if (state.regionResolved && document.documentElement.classList.contains('privacy-region-us-ca')) {
          applyCaliforniaConsent();
        }
      });
    }
  }

  function resolveRegion() {
    if (state.regionResolved) return;
    const region = readRegion();
    if (!region) return;
    state.regionResolved = true;
    if (isCaliforniaRegion(region)) {
      loadOneTrust();
      applyCaliforniaConsent();
      bindOneTrust();
      return;
    }
    scheduleTrackers();
    deferOneTrustUntilInteraction();
  }

  function confirmAndApply() {
    resolveRegion();
    window.setTimeout(() => {
      if (!document.documentElement.classList.contains('privacy-region-us-ca')) return;
      setConfirmedChoice(true);
      applyCaliforniaConsent();
    }, 100);
  }

  function rejectAndApply() {
    resolveRegion();
    window.setTimeout(() => {
      if (!document.documentElement.classList.contains('privacy-region-us-ca')) return;
      setConfirmedChoice(true);
      applyCaliforniaConsent();
    }, 100);
  }

  function closeWithoutChoice() {
    resolveRegion();
    window.setTimeout(() => {
      if (!document.documentElement.classList.contains('privacy-region-us-ca')) return;
      setConfirmedChoice(false);
      applyCaliforniaConsent();
    }, 100);
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-onetrust-preferences]')) {
      event.preventDefault();
      state.openPreferencesWhenReady = true;
      loadOneTrust();
      startOpenPreferencesLoop();
      return;
    }
    if (target.closest('#onetrust-accept-btn-handler, #save-preference-btn-handler, .save-preference-btn-handler')) {
      confirmAndApply();
      return;
    }
    if (target.closest('#onetrust-reject-all-handler, .ot-pc-refuse-all-handler')) {
      rejectAndApply();
      return;
    }
    if (target.closest('.onetrust-close-btn-handler')) closeWithoutChoice();
  }, true);

  document.addEventListener('brxl:one-trust-ready', () => {
    state.oneTrustReadyAt = Date.now();
    bindOneTrust();
    resolveRegion();
    startOpenPreferencesLoop();
  });

  document.addEventListener('visitorConsentCollected', () => {
    if (document.documentElement.classList.contains('privacy-region-us-ca')) applyCaliforniaConsent();
  });

  if (window.BRXLPrivacy && window.BRXLPrivacy.oneTrustReady) {
    state.oneTrustReadyAt = Date.now();
    bindOneTrust();
    resolveRegion();
  }

  whenDomReady.then(() => {
    resolveRegion();
    window.setTimeout(() => {
      if (state.regionResolved) return;
      const shopifyRegion = readShopifyRegion();
      if (isCaliforniaRegion(shopifyRegion)) {
        state.regionResolved = true;
        loadOneTrust();
        applyCaliforniaConsent();
        bindOneTrust();
        return;
      }
      if (shopifyRegion) {
        state.regionResolved = true;
        scheduleTrackers();
        deferOneTrustUntilInteraction();
        return;
      }
      loadOneTrust();
    }, 2500);
  });
})();
